/* eslint-disable react-hooks/set-state-in-effect -- score só faz sentido sincronizar com o ciclo de mode change */
import { useEffect, useRef, useState } from 'react';
import { useMode } from '@/hooks/useMode';

const SHOT_LIFETIME_MS = 450;
const BULLET_SPEED_PX_PER_SEC = 700;

const DESTROYABLE_TAGS = new Set([
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'P',
  'SPAN',
  'STRONG',
  'EM',
  'CODE',
  'A',
  'LI',
  'IMG',
  'SVG',
  'PICTURE',
  'BUTTON',
  'INPUT',
  'ARTICLE',
]);

const MAX_TARGET_AREA_RATIO = 0.3;

function spawnExplosion(x: number, y: number) {
  const node = document.createElement('span');
  node.className = 'gamer-shot';
  node.style.left = `${x}px`;
  node.style.top = `${y}px`;
  document.body.appendChild(node);
  window.setTimeout(() => node.remove(), SHOT_LIFETIME_MS);
}

function pickTarget(x: number, y: number): Element | null {
  const initial = document.elementFromPoint(x, y);
  if (!initial) return null;
  if (initial.closest('[data-gamer-immune]')) return null;

  let target: Element = initial;
  if (target.tagName.toLowerCase() !== 'svg' && target.closest('svg')) {
    const svg = target.closest('svg');
    if (svg) target = svg;
  }

  if (!DESTROYABLE_TAGS.has(target.tagName.toUpperCase())) return null;

  const rect = target.getBoundingClientRect();
  const viewportArea = window.innerWidth * window.innerHeight;
  if (rect.width * rect.height > viewportArea * MAX_TARGET_AREA_RATIO) return null;
  if (rect.width === 0 || rect.height === 0) return null;

  return target;
}

interface ShootContext {
  destroyed: Set<Element>;
  onHit: () => void;
}

function shootBullet(startX: number, startY: number, ctx: ShootContext) {
  const bullet = document.createElement('div');
  bullet.className = 'gamer-bullet';
  bullet.style.left = `${startX}px`;
  bullet.style.top = `${startY}px`;
  document.body.appendChild(bullet);

  const startTime = performance.now();
  let hit = false;

  function tick(now: number) {
    if (hit) return;
    const dt = (now - startTime) / 1000;
    const y = startY - BULLET_SPEED_PX_PER_SEC * dt;

    if (y < -40) {
      bullet.remove();
      return;
    }

    bullet.style.top = `${y}px`;

    const target = pickTarget(startX, y);
    if (target && !ctx.destroyed.has(target)) {
      hit = true;
      bullet.remove();
      spawnExplosion(startX, y);
      target.classList.add('gamer-destroyed');
      ctx.destroyed.add(target);
      ctx.onHit();
      return;
    }

    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
}

/**
 * Modo gamer: cursor vira navezinha, cada clique dispara um bullet que sobe.
 * Hit test contínuo durante o voo: se cruzar um elemento destrutível, esconde.
 * Se a bala sair pela parte de cima da tela, é um tiro perdido.
 */
export function GamerMode() {
  const { mode } = useMode();
  const [score, setScore] = useState(0);
  const destroyedRef = useRef<Set<Element>>(new Set());

  useEffect(() => {
    if (mode !== 'gamer') return;

    setScore(0);
    document.documentElement.classList.add('gamer-active');

    const destroyed = destroyedRef.current;

    const onClick = (event: MouseEvent) => {
      // Cancela navegação/submit quando o click é em algo que poderia ser
      // alvo — evita Link.onClick disparar antes da bala chegar.
      const initial = document.elementFromPoint(event.clientX, event.clientY);
      const inImmune = initial?.closest('[data-gamer-immune]');
      if (!inImmune) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      shootBullet(event.clientX, event.clientY, {
        destroyed,
        onHit: () => setScore((prev) => prev + 1),
      });
    };

    window.addEventListener('click', onClick, true);

    return () => {
      window.removeEventListener('click', onClick, true);
      document.documentElement.classList.remove('gamer-active');
      destroyed.forEach((el) => {
        el.classList.remove('gamer-destroyed');
      });
      destroyed.clear();
      document.querySelectorAll('.gamer-bullet').forEach((b) => b.remove());
      setScore(0);
    };
  }, [mode]);

  if (mode !== 'gamer') return null;

  return (
    <div
      data-gamer-immune
      className="pointer-events-none fixed top-4 left-1/2 z-40 -translate-x-1/2"
    >
      <div className="rounded-md border border-red-500/50 bg-black/80 px-4 py-2 font-mono text-sm tracking-widest text-red-400 uppercase backdrop-blur">
        Score: {String(score).padStart(4, '0')}
      </div>
    </div>
  );
}
